import React, { useEffect, useState, useRef } from "react";
import { api, fmtCurrency, whatsappLink } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Sparkles, Building2, Minus, Plus, CheckCircle2, MessageCircle, Calendar as CalendarIcon } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import BrandMark from "@/components/BrandMark";
import SocialLinks from "@/components/SocialLinks";

const EVENT_CATEGORIES = ["Wedding", "Corporate", "Birthday", "Housewarming", "Engagement", "Baby Full Moon", "Government", "School", "Others"];

const COUNTRY_CODES = [
  { code: "60", label: "🇲🇾 +60" },
  { code: "65", label: "🇸🇬 +65" },
  { code: "62", label: "🇮🇩 +62" },
  { code: "66", label: "🇹🇭 +66" },
  { code: "63", label: "🇵🇭 +63" },
  { code: "84", label: "🇻🇳 +84" },
  { code: "673", label: "🇧🇳 +673" },
  { code: "86", label: "🇨🇳 +86" },
  { code: "91", label: "🇮🇳 +91" },
  { code: "61", label: "🇦🇺 +61" },
  { code: "44", label: "🇬🇧 +44" },
  { code: "1", label: "🇺🇸 +1" },
];

const blankNormal = {
  invoice_name: "", contact_person: "", country_code: "60", phone: "", email: "", company_name: "",
  event_category: "Wedding", event_date: "", event_time: "12:00", pax: "", theme_colour: "",
  billing_address: "", delivery_address: "", same_address: true, remarks: "",
  billing_lat: null, billing_lng: null, delivery_lat: null, delivery_lng: null,
};
const blankCorp = {
  company_name: "", contact_person: "", country_code: "60", phone: "", email: "",
  event_date: "", event_time: "12:00", event_end_time: "", theme: "", address: "", pax: "", budget_per_pax: "", menu_notes: "",
  address_lat: null, address_lng: null,
};

function splitPhone(raw) {
  const digits = (raw || "").replace(/[^\d]/g, "");
  const codes = ["673", "60", "65", "62", "66", "63", "84", "86", "91", "61", "44", "1"];
  for (const c of codes) {
    if (digits.startsWith(c) && digits.length > c.length) return { code: c, local: digits.slice(c.length) };
  }
  return { code: "60", local: digits };
}

function PhoneField({ code, phone, onCode, onPhone, testid }) {
  return (
    <div className="flex gap-2 w-full">
      <select value={code} onChange={(e) => onCode(e.target.value)} data-testid={`${testid}-code`} className="inp" style={{ width: "92px", flex: "0 0 92px" }}>
        {COUNTRY_CODES.map((c) => <option key={c.code} value={c.code}>{c.label}</option>)}
      </select>
      <input required value={phone} onChange={(e) => onPhone(e.target.value.replace(/[^\d]/g, ""))} placeholder="123456789" data-testid={testid} className="inp" style={{ flex: "1 1 0%", minWidth: 0 }} />
    </div>
  );
}

// Address input with OpenStreetMap autocomplete — picking a suggestion pins exact coordinates.
function AddressField({ value, lat, onChange, onPick, testid, placeholder }) {
  const [sug, setSug] = useState([]);
  const timer = useRef();
  const handle = (v) => {
    onChange(v);
    clearTimeout(timer.current);
    if (!v || v.length < 4) { setSug([]); return; }
    timer.current = setTimeout(async () => {
      try {
        const r = await fetch(`https://nominatim.openstreetmap.org/search?format=json&limit=5&countrycodes=my&q=${encodeURIComponent(v)}`);
        const list = await r.json();
        setSug(Array.isArray(list) ? list : []);
      } catch (e) { console.warn("Address lookup failed:", e?.message); }
    }, 450);
  };
  return (
    <div>
      <div className="relative">
        <Textarea value={value} onChange={(e) => handle(e.target.value)} autoComplete="off"
          placeholder={placeholder || "Start typing to search address…"} data-testid={testid} className="inp" />
        {sug.length > 0 && (
          <div className="absolute z-50 top-full left-0 right-0 mt-1 bg-[#0a0a0a] border border-[#2a2a2a] rounded-sm shadow-xl max-h-52 overflow-y-auto" data-testid={`${testid}-suggestions`}>
            {sug.map((s, i) => (
              <button key={s.place_id || i} type="button" data-testid={`${testid}-sug-${i}`}
                onClick={() => { onPick({ text: s.display_name, lat: parseFloat(s.lat), lng: parseFloat(s.lon) }); setSug([]); }}
                className="w-full text-left px-3 py-2 text-xs text-zinc-300 hover:bg-[#141414] hover:text-[#D4AF37] border-b border-[#1a1a1a] last:border-0">
                {s.display_name}
              </button>
            ))}
          </div>
        )}
      </div>
      {lat != null && (
        <p className="text-[10px] text-emerald-400 mt-1" data-testid={`${testid}-pinned`}>✓ Exact location pinned — our delivery team will navigate precisely</p>
      )}
    </div>
  );
}

export default function PlanEventPage({ standalone = false, mode = "normal" }) {
  const { user } = useAuth();
  const [tab] = useState(mode);
  const [catalog, setCatalog] = useState([]);
  const [packages, setPackages] = useState([]);
  const [categoriesMeta, setCategoriesMeta] = useState([]);
  const [services, setServices] = useState([]);
  const [picked, setPicked] = useState({});          // item id -> qty
  const [pkgPicked, setPkgPicked] = useState({});     // package id -> bool
  const [pkgChoices, setPkgChoices] = useState({});   // pkgId -> { slotLabel: itemName }
  const [pkgVariants, setPkgVariants] = useState({}); // pkgId -> { slotKey: variant }
  const [pkgQty, setPkgQty] = useState({});           // pkgId -> number of sets (per-set packages)
  const [pkgCat, setPkgCat] = useState("");   // selected package category filter
  const [alaQ, setAlaQ] = useState("");       // à la carte search
  const [svc, setSvc] = useState({});                 // service name -> bool
  const [fn, setFn] = useState(blankNormal);
  const [fc, setFc] = useState(blankCorp);
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(null);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [company, setCompany] = useState(null);

  useEffect(() => {
    api.get("/company-settings").then((r) => setCompany(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    api.get("/public/menu").then((r) => { setCatalog(r.data.items || []); setPackages(r.data.packages || []); setCategoriesMeta(r.data.categories || []); }).catch(() => {});
    api.get("/public/services").then((r) => setServices(r.data || [])).catch(() => {});
    if (user && user.name) {
      const { code, local } = splitPhone(user.phone);
      setFn((f) => ({ ...f, invoice_name: user.name || "", contact_person: user.name || "", email: user.email || "", country_code: code, phone: local }));
      setFc((f) => ({ ...f, contact_person: user.name || "", email: user.email || "", country_code: code, phone: local }));
    }
  }, [user]);

  const setQty = (it, delta) => setPicked((p) => {
    const min = Math.max(1, it.min_order_qty || 1);
    const cur = p[it.id] || 0;
    let q = cur + delta;
    if (cur === 0 && delta > 0) q = min;
    if (q < min) q = 0;
    const next = { ...p };
    if (q <= 0) delete next[it.id]; else next[it.id] = q;
    return next;
  });


  const pkgCategories = [...new Set(packages.map((p) => p.category || "Others"))];
  const visiblePackages = pkgCat ? packages.filter((p) => (p.category || "Others") === pkgCat) : [];

  const togglePkg = (pkg) => {
    setPkgPicked((p) => ({ ...p, [pkg.id]: !p[pkg.id] }));
  };
  const setChoice = (pkgId, groupKey, name) => {
    setPkgChoices((c) => ({ ...c, [pkgId]: { ...c[pkgId], [groupKey]: name } }));
    setPkgVariants((v) => { const nv = { ...(v[pkgId] || {}) }; delete nv[groupKey]; return { ...v, [pkgId]: nv }; });
  };
  const setVariant = (pkgId, groupKey, variant) => setPkgVariants((v) => ({ ...v, [pkgId]: { ...(v[pkgId] || {}), [groupKey]: variant } }));

  const catName = (key) => {
    const c = categoriesMeta.find((x) => x.key === key);
    return c ? `${c.name_en}${c.name_cn ? ` ${c.name_cn}` : ""}` : key;
  };
  const groupDishes = (p, key) => {
    const manual = p.group_items?.[key] || [];
    return catalog.filter((it) => it.category === key && (manual.length === 0 || manual.includes(it.id)));
  };
  // Unified selection sections: custom manual sections take priority over auto menu groups
  const pkgSections = (p) => {
    if ((p.choice_slots || []).length > 0) {
      return p.choice_slots.map((s, i) => ({
        key: `s${i}`, label: s.label || `Section ${i + 1}`,
        selectable: s.selectable !== false,
        dishes: catalog.filter((it) => (s.option_ids || []).includes(it.id)),
      }));
    }
    return (p.menu_groups || []).map((g) => ({ key: g, label: catName(g), selectable: true, dishes: groupDishes(p, g) }));
  };
  const sectionDone = (p, sec) => {
    const chosen = (pkgChoices[p.id] || {})[sec.key];
    if (!chosen) return false;
    const dish = sec.dishes.find((d) => d.name === chosen);
    if (dish && (dish.variants || []).length > 0) return !!(pkgVariants[p.id] || {})[sec.key];
    return true;
  };
  const pkgProgress = (p) => {
    const sections = pkgSections(p).filter((sec) => sec.selectable);
    return { done: sections.filter((sec) => sectionDone(p, sec)).length, total: sections.length };
  };

  const currentPax = () => Number((tab === "corporate" ? fc.pax : fn.pax)) || 0;
  const isSetPkg = (p) => p.pricing_mode === "set";
  const qtyOf = (p) => Math.max(1, Number(pkgQty[p.id]) || 1);
  const bumpQty = (p, d) => setPkgQty((q) => ({ ...q, [p.id]: Math.max(1, qtyOf(p) + d) }));
  const pkgTotal = (p) => isSetPkg(p) ? (p.price_per_pax || 0) * qtyOf(p) : (p.price_per_pax || 0) * currentPax();
  const alaSubtotal = () => Object.entries(picked).reduce((s, [id, q]) => s + q * (catalog.find((c) => c.id === id)?.unit_price || 0), 0);
  const grandTotal = () => packages.filter((p) => pkgPicked[p.id]).reduce((sum, p) => sum + pkgTotal(p), 0) + alaSubtotal();

  const buildSelection = () => {
    const selectedPkgs = packages.filter((p) => pkgPicked[p.id]);
    return {
      items: Object.entries(picked).map(([id, q]) => {
        const c = catalog.find((x) => x.id === id);
        return { name: c?.name || id, quantity: q, uom: c?.uom || "pax", notes: "", unit_price: c?.unit_price || 0 };
      }),
      packages: selectedPkgs.map((p) => p.name),
      package_selections: selectedPkgs.map((p) => ({
        package_id: p.id,
        package_name: p.name,
        quantity: isSetPkg(p) ? qtyOf(p) : 1,
        pricing_mode: p.pricing_mode || "pax",
        unit_price: p.price_per_pax || 0,
        choices: pkgSections(p).flatMap((sec) => sec.selectable
          ? [{ slot: sec.label, item_name: (pkgChoices[p.id] || {})[sec.key], variant: (pkgVariants[p.id] || {})[sec.key] || "" }]
          : sec.dishes.map((d) => ({ slot: sec.label, item_name: d.name, variant: "" }))
        ).filter((c) => c.item_name),
      })),
    };
  };

  const incompletePackages = packages.filter((p) => pkgPicked[p.id]).filter((p) => {
    const { done, total } = pkgProgress(p);
    return total > 0 && done < total;
  });

  const validateAndReview = (e) => {
    e.preventDefault();
    const isCorp = tab === "corporate";
    const f = isCorp ? fc : fn;
    if (!f.event_date) return toast.error("Please pick an event date");
    if (!currentPax()) return toast.error("Please enter number of pax");
    if (incompletePackages.length > 0) {
      const p = incompletePackages[0];
      const { done, total } = pkgProgress(p);
      return toast.error(`Please complete your menu for "${p.name}" — ${done} of ${total} selections done`);
    }
    setReviewOpen(true);
  };

  const submit = async () => {
    const isCorp = tab === "corporate";
    setSaving(true);
    try {
      const sel = buildSelection();
      const selectedServices = services.filter((s) => svc[s.name]).map((s) => s.name);
      let payload;
      if (isCorp) {
        const fullPhone = `${fc.country_code}${fc.phone}`;
        payload = { type: "corporate", name: fc.contact_person || fc.company_name, ...fc, phone: fullPhone, pax: Number(fc.pax) || 0, budget_per_pax: fc.budget_per_pax ? Number(fc.budget_per_pax) : null, services: selectedServices, delivery_lat: fc.address_lat, delivery_lng: fc.address_lng, ...sel };
        delete payload.country_code;
        delete payload.address_lat;
        delete payload.address_lng;
      } else {
        const delivery = fn.same_address ? fn.billing_address : fn.delivery_address;
        const dLat = fn.same_address ? fn.billing_lat : fn.delivery_lat;
        const dLng = fn.same_address ? fn.billing_lng : fn.delivery_lng;
        const fullPhone = `${fn.country_code}${fn.phone}`;
        payload = {
          type: "normal", name: fn.contact_person || fn.invoice_name, ...fn, phone: fullPhone,
          address: delivery, delivery_address: delivery, delivery_lat: dLat, delivery_lng: dLng, pax: Number(fn.pax) || 0,
          services: selectedServices, ...sel,
        };
        delete payload.same_address;
        delete payload.country_code;
        delete payload.billing_lat;
        delete payload.billing_lng;
      }
      const { data } = await api.post("/enquiries", payload);
      setReviewOpen(false);
      setDone(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to submit");
    } finally { setSaving(false); }
  };

  const buildWaMessage = (e) => {
    const isCorp = e.type === "corporate";
    const lines = [
      `*New Event Order — ${e.ref_code}*`,
      `Name: ${e.invoice_name || e.name}`,
      isCorp && e.company_name ? `Company: ${e.company_name}` : "",
      e.event_category ? `Category: ${e.event_category}` : "",
      e.event_name ? `Event: ${e.event_name}` : "",
      `Date: ${e.event_date}${e.event_time ? ` ${e.event_time}` : ""}${e.event_end_time ? ` - ${e.event_end_time}` : ""}`,
      `Pax: ${e.pax}`,
      e.menu_notes ? `Menu: ${e.menu_notes}` : "",
      e.theme ? `Theme: ${e.theme}` : "",
      e.theme_colour ? `Theme colour: ${e.theme_colour}` : "",
      e.budget_per_pax ? `Budget/pax: RM${e.budget_per_pax}` : "",
      (e.services || []).length ? `Services: ${e.services.join(", ")}` : "",
    ].filter(Boolean);
    (e.package_selections || []).forEach((ps) => {
      lines.push(`\n*${ps.package_name}*${ps.pricing_mode === "set" ? ` × ${ps.quantity || 1} set(s)` : ""}`);
      (ps.choices || []).forEach((c) => lines.push(`• ${c.slot}: ${c.item_name}${c.variant ? ` (${c.variant})` : ""}`));
    });
    if ((e.remarks || e.requirements)) lines.push(`\nRemarks: ${e.remarks || e.requirements}`);
    return lines.join("\n");
  };

  if (done) {
    const companyPhone = (company?.phone || "").replace(/[^\d]/g, "");
    const waHref = companyPhone ? whatsappLink(companyPhone, buildWaMessage(done)) : null;
    const success = (
      <div className="p-6 md:p-10 max-w-2xl mx-auto text-center" data-testid="enquiry-success">
        <CheckCircle2 className="w-12 h-12 text-emerald-500 mx-auto mb-4" />
        <h1 className="font-display text-4xl">Request received!</h1>
        <p className="text-zinc-400 mt-3">Your reference code:</p>
        <div className="font-display text-3xl text-[#D4AF37] tracking-widest mt-2" data-testid="enquiry-ref-code">{done.ref_code}</div>
        <div className="mt-6 p-4 rounded-sm border border-emerald-500/40 bg-emerald-500/5">
          <p className="text-sm text-zinc-300 font-medium">One last step — send your order to us on WhatsApp to confirm</p>
          <p className="text-xs text-zinc-500 mt-1">Tap the button below so our team receives your order instantly.</p>
          {waHref ? (
            <a href={waHref} target="_blank" rel="noreferrer" data-testid="wa-send-order"
              className="inline-flex items-center gap-2 mt-3 px-6 py-3 rounded-sm bg-emerald-600 hover:bg-emerald-500 text-white font-medium transition-colors">
              <MessageCircle className="w-4 h-4" /> Send Order via WhatsApp
            </a>
          ) : (
            <p className="text-xs text-amber-400 mt-3">Our team will contact you shortly.</p>
          )}
        </div>
        <p className="text-zinc-500 text-xs mt-5">
          {user && user.name ? "You can also follow its status on My Events." : "Please save your reference code."}
        </p>
        <div className="flex gap-2 justify-center mt-6">
          <Button onClick={() => { setDone(null); setPicked({}); setPkgPicked({}); setPkgChoices({}); setPkgVariants({}); setPkgQty({}); setSvc({}); }} variant="outline" className="border-[#2a2a2a]">Submit another</Button>
          {user && user.name && (
            <Button onClick={() => (window.location.href = "/portal")} data-testid="back-to-portal" className="bg-[#D4AF37] text-black hover:bg-[#E5C86B]">Go to My Events</Button>
          )}
        </div>
      </div>
    );
    return standalone ? <StandaloneShell>{success}</StandaloneShell> : success;
  }

  const content = (
    <div className="p-6 md:p-10 max-w-4xl mx-auto" data-testid="plan-event-page">
      <div className="mb-8">
        <div className="label-eyebrow">{tab === "corporate" ? "Event Planners" : "HNC Customer Order"}</div>
        <h1 className="font-display text-4xl md:text-5xl mt-2">{tab === "corporate" ? "Corporate Quotation" : "HNC Customer Order"}</h1>
        <p className="text-zinc-500 mt-2 text-sm">Tell us what you're dreaming of — we'll take care of the rest.</p>
      </div>

      <form onSubmit={validateAndReview} className="space-y-6">
        {tab === "normal" ? (
          <>
            {/* Customer Information */}
            <Section title="Customer Information">
              <Field label="Invoice Name"><Input required value={fn.invoice_name} onChange={(e) => setFn({ ...fn, invoice_name: e.target.value })} data-testid="enq-invoice-name" className="inp" /></Field>
              <Field label="Contact Person"><Input required value={fn.contact_person} onChange={(e) => setFn({ ...fn, contact_person: e.target.value })} data-testid="enq-contact" className="inp" /></Field>
              <div className="sm:col-span-2"><Field label="Contact Number"><PhoneField code={fn.country_code} phone={fn.phone} onCode={(v) => setFn({ ...fn, country_code: v })} onPhone={(v) => setFn({ ...fn, phone: v })} testid="enq-phone" /></Field></div>
              <div className="sm:col-span-2"><Field label="Email"><Input type="email" value={fn.email} onChange={(e) => setFn({ ...fn, email: e.target.value })} className="inp" /></Field></div>
              <Field label="Company Name (Optional)"><Input value={fn.company_name} onChange={(e) => setFn({ ...fn, company_name: e.target.value })} className="inp" /></Field>
            </Section>

            {/* Event Information */}
            <Section title="Event Information">
              <Field label="Event Category">
                <select value={fn.event_category} onChange={(e) => setFn({ ...fn, event_category: e.target.value })} data-testid="enq-category" className="inp w-full">
                  {EVENT_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </Field>
              <Field label="Number of Pax"><Input type="number" min="0" required value={fn.pax} onChange={(e) => setFn({ ...fn, pax: e.target.value })} data-testid="enq-pax" className="inp" /></Field>
              <Field label="Event Date"><DatePicker value={fn.event_date} onChange={(v) => setFn({ ...fn, event_date: v })} testid="enq-date" /></Field>
              <Field label="Event Time"><Input type="time" value={fn.event_time} onChange={(e) => setFn({ ...fn, event_time: e.target.value })} className="inp" /></Field>
              <div className="sm:col-span-2"><Field label="Event Theme Colour (Optional)"><Input value={fn.theme_colour} onChange={(e) => setFn({ ...fn, theme_colour: e.target.value })} placeholder="e.g. Gold & White, Tiffany Blue" data-testid="enq-theme-colour" className="inp" /></Field></div>
              <div className="sm:col-span-2"><Field label="Billing Address">
                <AddressField value={fn.billing_address} lat={fn.billing_lat} testid="enq-billing"
                  onChange={(v) => setFn({ ...fn, billing_address: v, billing_lat: null, billing_lng: null })}
                  onPick={({ text, lat, lng }) => setFn({ ...fn, billing_address: text, billing_lat: lat, billing_lng: lng })} />
              </Field></div>
              <div className="sm:col-span-2">
                <label className="flex items-center gap-2 text-xs text-zinc-400 cursor-pointer mb-2">
                  <input type="checkbox" checked={fn.same_address} onChange={(e) => setFn({ ...fn, same_address: e.target.checked })} data-testid="enq-same-address" className="accent-[#D4AF37]" />
                  Delivery address same as billing address
                </label>
                {!fn.same_address && (
                  <Field label="Delivery Address">
                    <AddressField value={fn.delivery_address} lat={fn.delivery_lat} testid="enq-delivery"
                      onChange={(v) => setFn({ ...fn, delivery_address: v, delivery_lat: null, delivery_lng: null })}
                      onPick={({ text, lat, lng }) => setFn({ ...fn, delivery_address: text, delivery_lat: lat, delivery_lng: lng })} />
                  </Field>
                )}
              </div>
            </Section>
          </>
        ) : (
          <>
          <Section title="Corporate Details">
            <Field label="Company Name"><Input required value={fc.company_name} onChange={(e) => setFc({ ...fc, company_name: e.target.value })} data-testid="enq-company" className="inp" /></Field>
            <Field label="PIC Name"><Input required value={fc.contact_person} onChange={(e) => setFc({ ...fc, contact_person: e.target.value })} data-testid="enq-corp-contact" className="inp" /></Field>
            <div className="sm:col-span-2"><Field label="Phone (WhatsApp)"><PhoneField code={fc.country_code} phone={fc.phone} onCode={(v) => setFc({ ...fc, country_code: v })} onPhone={(v) => setFc({ ...fc, phone: v })} testid="enq-corp-phone" /></Field></div>
            <div className="sm:col-span-2"><Field label="Email"><Input type="email" value={fc.email} onChange={(e) => setFc({ ...fc, email: e.target.value })} className="inp" /></Field></div>
            <Field label="Event Date"><DatePicker value={fc.event_date} onChange={(v) => setFc({ ...fc, event_date: v })} testid="enq-corp-date" /></Field>
            <Field label="Event Time">
              <div className="flex items-center gap-2">
                <Input type="time" value={fc.event_time} onChange={(e) => setFc({ ...fc, event_time: e.target.value })} data-testid="enq-corp-time-start" className="inp" />
                <span className="text-zinc-500 text-sm shrink-0">–</span>
                <Input type="time" value={fc.event_end_time} onChange={(e) => setFc({ ...fc, event_end_time: e.target.value })} data-testid="enq-corp-time-end" className="inp" />
              </div>
            </Field>
            <div className="sm:col-span-2"><Field label="Event Address">
              <AddressField value={fc.address} lat={fc.address_lat} testid="enq-venue"
                onChange={(v) => setFc({ ...fc, address: v, address_lat: null, address_lng: null })}
                onPick={({ text, lat, lng }) => setFc({ ...fc, address: text, address_lat: lat, address_lng: lng })} />
            </Field></div>
            <Field label="Estimated Pax"><Input type="number" min="0" required value={fc.pax} onChange={(e) => setFc({ ...fc, pax: e.target.value })} data-testid="enq-corp-pax" className="inp" /></Field>
          </Section>

          <Section title="Menu & Budget">
            <div className="sm:col-span-2"><Field label="Menu">
              <Textarea value={fc.menu_notes} onChange={(e) => setFc({ ...fc, menu_notes: e.target.value })} placeholder="Describe the menu you have in mind — or pick from our packages below" data-testid="enq-menu-notes" className="inp" />
            </Field></div>
            <Field label="Budget / Pax (RM)"><Input type="number" min="0" step="0.01" value={fc.budget_per_pax} onChange={(e) => setFc({ ...fc, budget_per_pax: e.target.value })} data-testid="enq-budget" className="inp" /></Field>
            <Field label="Theme"><Input value={fc.theme} onChange={(e) => setFc({ ...fc, theme: e.target.value })} placeholder="e.g. Gatsby Gold" data-testid="enq-theme" className="inp" /></Field>
          </Section>
          </>
        )}

        {/* Services Required */}
        {services.length > 0 && (
          <div className="card-lux p-6">
            <div className="label-eyebrow">Services Required</div>
            <h3 className="font-display text-xl mt-1 mb-4">Select all that apply</h3>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {services.map((s) => (
                <label key={s.id} className={`flex items-center gap-2 p-3 rounded-sm border cursor-pointer text-sm transition-colors ${svc[s.name] ? "border-[#D4AF37] bg-[#D4AF37]/5 text-[#D4AF37]" : "border-[#2a2a2a] text-zinc-300 hover:border-[#3a3a3a]"}`} data-testid={`enq-service-${s.id}`}>
                  <input type="checkbox" checked={!!svc[s.name]} onChange={(e) => setSvc({ ...svc, [s.name]: e.target.checked })} className="accent-[#D4AF37]" />
                  {s.name}
                </label>
              ))}
            </div>
          </div>
        )}

        {/* Packages grouped by category — pick a category first */}
        {packages.length > 0 && (
          <div className="card-lux p-6">
            <div className="label-eyebrow">Packages</div>
            <h3 className="font-display text-xl mt-1 mb-4">Curated bundles</h3>
            <div className="flex items-center gap-2 flex-wrap mb-4" data-testid="pkg-category-chips">
              {pkgCategories.map((c) => (
                <button key={c} type="button" onClick={() => setPkgCat(pkgCat === c ? "" : c)} data-testid={`pkg-cat-${c.replace(/\s+/g, "-")}`}
                  className={`px-3 py-1.5 text-xs rounded-sm border transition-colors ${pkgCat === c ? "bg-[#D4AF37] text-black border-[#D4AF37]" : "border-[#2a2a2a] text-zinc-400 hover:border-[#D4AF37] hover:text-[#D4AF37]"}`}>
                  {c}
                </button>
              ))}
            </div>
            {!pkgCat && <div className="text-xs text-zinc-600 py-2">Select a category above to see its packages.</div>}
            <div className="space-y-3">
              {visiblePackages.map((p) => (
                <div key={p.id} className={`rounded-sm border transition-colors ${pkgPicked[p.id] ? "border-[#D4AF37]" : "border-[#2a2a2a]"}`} data-testid={`enq-pkg-${p.id}`}>
                  <label className="flex items-start justify-between gap-3 p-4 cursor-pointer">
                    <div>
                      <div className="font-display">{p.name}</div>
                      <div className="text-xs text-zinc-500 mt-0.5">{p.description || `${(p.items || []).length} dishes`}{!isSetPkg(p) && p.min_pax ? ` · min ${p.min_pax} pax` : ""}</div>
                    </div>
                    <div className="text-right shrink-0">
                      <div className="text-[#D4AF37] text-sm">{fmtCurrency(p.price_per_pax)}/{isSetPkg(p) ? "set" : "pax"}</div>
                      {pkgPicked[p.id] && (isSetPkg(p) || currentPax() > 0) && (
                        <div className="text-[10px] text-emerald-400 mt-0.5" data-testid={`enq-pkg-total-${p.id}`}>
                          {isSetPkg(p) ? `${qtyOf(p)} set${qtyOf(p) > 1 ? "s" : ""} ≈ ${fmtCurrency(pkgTotal(p))}` : `${currentPax()} pax ≈ ${fmtCurrency(pkgTotal(p))}`}
                        </div>
                      )}
                      <input type="checkbox" checked={!!pkgPicked[p.id]} onChange={() => togglePkg(p)} data-testid={`enq-pkg-check-${p.id}`} className="accent-[#D4AF37] mt-1" />
                    </div>
                  </label>
                  {pkgPicked[p.id] && isSetPkg(p) && (
                    <div className="px-4 pb-3 flex items-center gap-3" data-testid={`enq-pkg-qty-${p.id}`}>
                      <span className="text-xs text-zinc-400">Number of sets</span>
                      <div className="flex items-center gap-2">
                        <button type="button" onClick={() => bumpQty(p, -1)} data-testid={`enq-pkg-qty-minus-${p.id}`} disabled={qtyOf(p) <= 1}
                          className="w-7 h-7 rounded-sm border border-[#2a2a2a] flex items-center justify-center text-zinc-300 hover:border-[#D4AF37] hover:text-[#D4AF37] disabled:opacity-40 transition-colors">
                          <Minus className="w-3.5 h-3.5" />
                        </button>
                        <span className="w-8 text-center text-sm text-[#D4AF37] font-medium" data-testid={`enq-pkg-qty-value-${p.id}`}>{qtyOf(p)}</span>
                        <button type="button" onClick={() => bumpQty(p, 1)} data-testid={`enq-pkg-qty-plus-${p.id}`}
                          className="w-7 h-7 rounded-sm border border-[#2a2a2a] flex items-center justify-center text-zinc-300 hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors">
                          <Plus className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>
                  )}
                  {pkgPicked[p.id] && pkgSections(p).length > 0 && (
                    <div className="px-4 pb-4 pt-1 border-t border-[#1a1a1a]" data-testid={`enq-pkg-choices-${p.id}`}>
                      {/* Progress indicator */}
                      <div className="sticky top-0 z-10 bg-[#0a0a0a]/95 backdrop-blur-sm py-2 -mx-1 px-1 mb-2">
                        <div className="flex items-center justify-between text-xs mb-1.5">
                          <span className="text-zinc-400">Build your menu — choose ONE dish per group</span>
                          <span className={pkgProgress(p).done === pkgProgress(p).total ? "text-emerald-400" : "text-[#D4AF37]"} data-testid={`enq-pkg-progress-${p.id}`}>
                            {pkgProgress(p).done} of {pkgProgress(p).total} selections completed
                          </span>
                        </div>
                        <div className="h-1 rounded-full bg-[#1a1a1a] overflow-hidden">
                          <div className="h-full bg-[#D4AF37] transition-all duration-300" style={{ width: `${pkgProgress(p).total ? (pkgProgress(p).done / pkgProgress(p).total) * 100 : 0}%` }} />
                        </div>
                      </div>

                      {/* One section per menu group — radio selection cards */}
                      <div className="space-y-5 mt-3">
                        {pkgSections(p).map((sec, gi) => {
                          const dishes = sec.dishes;
                          const chosen = (pkgChoices[p.id] || {})[sec.key];
                          if (!sec.selectable) {
                            return (
                              <div key={sec.key} data-testid={`enq-group-${p.id}-${sec.key}`}>
                                <div className="flex items-center gap-2 mb-2">
                                  <span className="w-5 h-5 rounded-full flex items-center justify-center bg-[#D4AF37]/20 text-[#D4AF37] text-[10px]">✓</span>
                                  <span className="label-eyebrow text-[11px]">{sec.label}</span>
                                  <span className="text-[10px] text-zinc-600">Included</span>
                                </div>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                  {dishes.map((it) => (
                                    <div key={it.id} className="flex items-center gap-2.5 p-3 rounded-sm border border-[#2a2a2a] text-sm text-zinc-300" data-testid={`enq-fixed-${p.id}-${it.id}`}>
                                      <span className="w-1.5 h-1.5 rounded-full bg-[#D4AF37]" />
                                      <span>{it.name}{it.name_cn ? <span className="text-zinc-500 text-xs"> · {it.name_cn}</span> : null}</span>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            );
                          }
                          return (
                            <div key={sec.key} data-testid={`enq-group-${p.id}-${sec.key}`}>
                              <div className="flex items-center gap-2 mb-2">
                                <span className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-medium ${chosen ? "bg-emerald-500 text-black" : "bg-[#1a1a1a] text-zinc-400 border border-[#2a2a2a]"}`}>
                                  {chosen ? "✓" : gi + 1}
                                </span>
                                <span className="label-eyebrow text-[11px]">{sec.label}</span>
                                <span className="text-[10px] text-zinc-600">Choose ONE</span>
                              </div>
                              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" role="radiogroup">
                                {dishes.map((it) => (
                                  <label key={it.id} data-testid={`enq-radio-${p.id}-${it.id}`}
                                    className={`flex items-center gap-2.5 p-3 rounded-sm border cursor-pointer text-sm transition-colors ${chosen === it.name ? "border-[#D4AF37] bg-[#D4AF37]/10 text-[#D4AF37]" : "border-[#2a2a2a] text-zinc-300 hover:border-[#3a3a3a]"}`}>
                                    <input type="radio" name={`grp-${p.id}-${sec.key}`} checked={chosen === it.name} onChange={() => setChoice(p.id, sec.key, it.name)} className="accent-[#D4AF37]" />
                                    <span>{it.name}{it.name_cn ? <span className="text-zinc-500 text-xs"> · {it.name_cn}</span> : null}</span>
                                  </label>
                                ))}
                                {dishes.length === 0 && <div className="text-xs text-zinc-600 col-span-2">No dishes available in this section yet.</div>}
                              </div>
                              {(() => {
                                const dish = dishes.find((d) => d.name === chosen);
                                if (!dish || !(dish.variants || []).length) return null;
                                const curVar = (pkgVariants[p.id] || {})[sec.key];
                                return (
                                  <div className="mt-2 ml-7 p-3 rounded-sm border border-[#D4AF37]/30 bg-[#D4AF37]/5" data-testid={`enq-variants-${p.id}-${sec.key}`}>
                                    <div className="text-[10px] uppercase tracking-widest text-[#D4AF37] mb-2">{dish.name} — choose a variant</div>
                                    <div className="flex flex-wrap gap-2">
                                      {dish.variants.map((v) => (
                                        <label key={v} data-testid={`enq-variant-${p.id}-${dish.id}-${v.replace(/\s+/g, "-")}`}
                                          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-sm border cursor-pointer text-xs transition-colors ${curVar === v ? "border-[#D4AF37] bg-[#D4AF37]/15 text-[#D4AF37]" : "border-[#2a2a2a] text-zinc-300 hover:border-[#3a3a3a]"}`}>
                                          <input type="radio" name={`var-${p.id}-${sec.key}`} checked={curVar === v} onChange={() => setVariant(p.id, sec.key, v)} className="accent-[#D4AF37]" />
                                          {v}
                                        </label>
                                      ))}
                                    </div>
                                    {!curVar && <p className="text-[10px] text-amber-400 mt-1.5">Please pick a variant to complete this selection.</p>}
                                  </div>
                                );
                              })()}
                            </div>
                          );
                        })}
                      </div>

                      {/* Fixed package items are internal (kitchen prep) — not shown to customers */}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* À la carte — individual dishes with min order quantities */}
        {catalog.length > 0 && (
          <div className="card-lux p-6" data-testid="ala-carte-section">
            <div className="label-eyebrow">À La Carte</div>
            <h3 className="font-display text-xl mt-1 mb-1">Individual dishes</h3>
            <p className="text-xs text-zinc-500 mb-3">Add single dishes on top of (or instead of) a package.</p>
            <Input value={alaQ} onChange={(e) => setAlaQ(e.target.value)} placeholder="Search dishes…" data-testid="ala-search" className="inp mb-3" />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-80 overflow-y-auto pr-1">
              {catalog
                .filter((it) => it.alacarte_active !== false)
                .filter((it) => { const q = alaQ.trim().toLowerCase(); return !q || (it.name || "").toLowerCase().includes(q) || (it.name_cn || "").includes(alaQ.trim()); })
                .map((it) => {
                  const min = Math.max(1, it.min_order_qty || 1);
                  const q = picked[it.id] || 0;
                  return (
                    <div key={it.id} className={`flex items-center justify-between gap-2 p-3 rounded-sm border transition-colors ${q > 0 ? "border-[#D4AF37]" : "border-[#2a2a2a]"}`} data-testid={`ala-item-${it.id}`}>
                      <div className="min-w-0">
                        <div className="text-sm text-zinc-200 truncate">{it.name}{it.name_cn ? <span className="text-zinc-500 text-xs"> · {it.name_cn}</span> : null}</div>
                        <div className="text-[10px] text-zinc-500">{fmtCurrency(it.unit_price)} / {it.uom || "pax"}{min > 1 ? <span className="text-amber-400"> · min {min}</span> : null}</div>
                      </div>
                      <div className="flex items-center gap-1.5 shrink-0">
                        <button type="button" onClick={() => setQty(it, -1)} disabled={q === 0} data-testid={`ala-minus-${it.id}`}
                          className="w-7 h-7 rounded-sm border border-[#2a2a2a] flex items-center justify-center text-zinc-300 hover:border-[#D4AF37] hover:text-[#D4AF37] disabled:opacity-40 transition-colors">
                          <Minus className="w-3.5 h-3.5" />
                        </button>
                        <span className={`w-9 text-center text-sm font-medium ${q > 0 ? "text-[#D4AF37]" : "text-zinc-600"}`} data-testid={`ala-qty-${it.id}`}>{q}</span>
                        <button type="button" onClick={() => setQty(it, 1)} data-testid={`ala-plus-${it.id}`}
                          className="w-7 h-7 rounded-sm border border-[#2a2a2a] flex items-center justify-center text-zinc-300 hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors">
                          <Plus className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>
                  );
                })}
            </div>
            {alaSubtotal() > 0 && (
              <div className="mt-3 pt-3 border-t border-[#1a1a1a] flex justify-between text-sm">
                <span className="text-zinc-400">À la carte subtotal</span>
                <span className="text-[#D4AF37]" data-testid="ala-subtotal">{fmtCurrency(alaSubtotal())}</span>
              </div>
            )}
          </div>
        )}

        {/* Selected menu summary */}
        {packages.some((p) => pkgPicked[p.id]) && (
          <div className="card-lux p-6 border-[#D4AF37]/40" data-testid="selected-menu-summary">
            <div className="label-eyebrow">Summary</div>
            <h3 className="font-display text-xl mt-1 mb-4">Your Selected Menu</h3>
            {packages.filter((p) => pkgPicked[p.id]).map((p) => {
              const chosen = pkgChoices[p.id] || {};
              const { done, total } = pkgProgress(p);
              return (
                <div key={p.id} className="mb-4 last:mb-0">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm text-[#D4AF37] font-medium">
                      {p.name} · {fmtCurrency(p.price_per_pax)}/{isSetPkg(p) ? "set" : "pax"}
                      {isSetPkg(p) ? ` × ${qtyOf(p)} set${qtyOf(p) > 1 ? "s" : ""} ≈ ${fmtCurrency(pkgTotal(p))}` : (currentPax() > 0 ? ` × ${currentPax()} pax ≈ ${fmtCurrency(pkgTotal(p))}` : "")}
                    </span>
                    {total > 0 && <span className={`text-[10px] ${done === total ? "text-emerald-400" : "text-amber-400"}`}>{done === total ? "✓ Complete" : `${done}/${total} selected`}</span>}
                  </div>
                  <div className="space-y-1">
                    {pkgSections(p).map((sec) => (
                      <div key={sec.key} className="flex items-center gap-2 text-sm">
                        {sec.selectable ? (
                          <>
                            <span className={`w-1.5 h-1.5 rounded-full ${sectionDone(p, sec) ? "bg-emerald-400" : "bg-zinc-700"}`} />
                            <span className="text-zinc-500 text-xs w-32 shrink-0">{sec.label}</span>
                            <span className={chosen[sec.key] ? "text-zinc-200" : "text-zinc-600 italic"}>
                              {chosen[sec.key] ? `${chosen[sec.key]}${(pkgVariants[p.id] || {})[sec.key] ? ` — ${(pkgVariants[p.id] || {})[sec.key]}` : ""}` : "not selected yet"}
                            </span>
                          </>
                        ) : (
                          <>
                            <span className="w-1.5 h-1.5 rounded-full bg-[#D4AF37]" />
                            <span className="text-zinc-500 text-xs w-32 shrink-0">{sec.label}</span>
                            <span className="text-zinc-200">{sec.dishes.map((d) => d.name).join(", ")}</span>
                          </>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Special Requests — customer orders only */}
        {tab === "normal" && (
        <div className="card-lux p-6">
          <div className="label-eyebrow">Special Requests</div>
          <h3 className="font-display text-xl mt-1 mb-3">Anything else?</h3>
          <Textarea value={fn.remarks} onChange={(e) => setFn({ ...fn, remarks: e.target.value })} placeholder="No Beef, Vegetarian meals, allergy info, extra tables…" data-testid="enq-remarks" className="inp min-h-24" />
        </div>
        )}

        <Button type="submit" disabled={saving} data-testid="enq-submit" className="w-full h-12 bg-[#D4AF37] text-black hover:bg-[#E5C86B] rounded-sm font-medium">
          {tab === "corporate" ? "Review Quotation Request" : "Review My Order"}
        </Button>
      </form>

      <ReviewDialog open={reviewOpen} onOpenChange={setReviewOpen} onConfirm={submit} saving={saving}
        tab={tab} fn={fn} fc={fc} services={services} svc={svc}
        selectedPackages={packages.filter((p) => pkgPicked[p.id])} pkgSections={pkgSections} pkgChoices={pkgChoices} pkgVariants={pkgVariants} pkgQty={pkgQty}
        alaItems={Object.entries(picked).map(([id, q]) => { const c = catalog.find((x) => x.id === id); return { name: c?.name || id, qty: q, total: q * (c?.unit_price || 0) }; })}
        pkgTotal={pkgTotal} grandTotal={grandTotal} pax={currentPax()} />
    </div>
  );

  return standalone ? <StandaloneShell>{content}</StandaloneShell> : content;
}

function ReviewDialog({ open, onOpenChange, onConfirm, saving, tab, fn, fc, services, svc, selectedPackages, pkgSections, pkgChoices, pkgVariants, pkgQty, alaItems = [], pkgTotal, grandTotal, pax }) {
  const isCorp = tab === "corporate";
  const selectedServices = services.filter((s) => svc[s.name]).map((s) => s.name);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0a0a0a] border-[#2a2a2a] max-w-lg max-h-[85vh] overflow-y-auto" data-testid="review-dialog">
        <DialogHeader><DialogTitle className="font-display text-2xl">Review before submitting</DialogTitle></DialogHeader>
        <div className="space-y-4 text-sm">
          <div>
            <div className="label-eyebrow text-[10px] mb-1">{isCorp ? "Company" : "Customer"}</div>
            {isCorp ? (
              <>
                <Row k="Company" v={fc.company_name} />
                <Row k="PIC" v={fc.contact_person} />
                <Row k="Phone" v={`+${fc.country_code} ${fc.phone}`} />
                <Row k="Address" v={fc.address} />
                <Row k="Menu" v={fc.menu_notes} />
                <Row k="Theme" v={fc.theme} />
                {fc.budget_per_pax && <Row k="Budget/pax" v={`RM${fc.budget_per_pax}`} />}
              </>
            ) : (
              <>
                <Row k="Invoice Name" v={fn.invoice_name} />
                <Row k="Contact" v={fn.contact_person} />
                <Row k="Phone" v={`+${fn.country_code} ${fn.phone}`} />
                <Row k="Category" v={fn.event_category} />
                {fn.theme_colour && <Row k="Theme Colour" v={fn.theme_colour} />}
                <Row k="Billing" v={fn.billing_address} />
                <Row k="Delivery" v={fn.same_address ? "Same as billing" : fn.delivery_address} />
              </>
            )}
            <Row k="Date" v={`${isCorp ? fc.event_date : fn.event_date}${(isCorp ? fc.event_time : fn.event_time) ? ` · ${isCorp ? fc.event_time : fn.event_time}` : ""}${isCorp && fc.event_end_time ? ` – ${fc.event_end_time}` : ""}`} />
            <Row k="Pax" v={pax} />
          </div>
          {selectedServices.length > 0 && (
            <div><div className="label-eyebrow text-[10px] mb-1">Services</div><div className="text-zinc-300">{selectedServices.join(", ")}</div></div>
          )}
          {selectedPackages.map((p) => (
            <div key={p.id} className="border-t border-[#1a1a1a] pt-3">
              <div className="flex justify-between text-[#D4AF37]"><span>{p.name}</span><span>{fmtCurrency(pkgTotal(p))}</span></div>
              <div className="text-[10px] text-zinc-500 mb-1">
                {p.pricing_mode === "set"
                  ? `${fmtCurrency(p.price_per_pax)}/set × ${Math.max(1, Number(pkgQty?.[p.id]) || 1)} set(s)`
                  : `${fmtCurrency(p.price_per_pax)}/pax × ${pax} pax`}
              </div>
              {pkgSections(p).map((sec) => {
                const nm = (pkgChoices[p.id] || {})[sec.key];
                const vr = (pkgVariants?.[p.id] || {})[sec.key];
                return (
                  <div key={sec.key} className="flex gap-2 text-xs py-0.5">
                    <span className="text-zinc-500 w-28 shrink-0">{sec.label}</span>
                    <span className="text-zinc-200">{sec.selectable ? (nm ? `${nm}${vr ? ` — ${vr}` : ""}` : "—") : sec.dishes.map((d) => d.name).join(", ")}</span>
                  </div>
                );
              })}
            </div>
          ))}
          {alaItems.length > 0 && (
            <div className="border-t border-[#1a1a1a] pt-3">
              <div className="label-eyebrow text-[10px] mb-1">À La Carte</div>
              {alaItems.map((i) => (
                <div key={i.name} className="flex justify-between text-xs py-0.5">
                  <span className="text-zinc-300">{i.name} × {i.qty}</span>
                  <span className="text-zinc-200">{fmtCurrency(i.total)}</span>
                </div>
              ))}
            </div>
          )}
          {selectedPackages.length > 0 && (
            <div className="border-t border-[#2a2a2a] pt-2 flex justify-between font-medium">
              <span>Estimated Total</span><span className="text-[#D4AF37]" data-testid="review-total">{fmtCurrency(grandTotal())}</span>
            </div>
          )}
          <p className="text-[10px] text-zinc-500">Estimate only — final quotation confirmed by our team.</p>
        </div>
        <div className="flex gap-2 pt-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} className="border-[#2a2a2a] flex-1" data-testid="review-back">Back to edit</Button>
          <Button onClick={onConfirm} disabled={saving} data-testid="review-confirm" className="bg-[#D4AF37] text-black hover:bg-[#E5C86B] flex-1">{saving ? "Submitting…" : "Confirm & Submit"}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Row({ k, v }) {
  if (!v) return null;
  return <div className="flex gap-2 py-0.5"><span className="text-zinc-500 w-28 shrink-0">{k}</span><span className="text-zinc-200 flex-1">{v}</span></div>;
}

function StandaloneShell({ children }) {
  return (
    <div className="min-h-screen bg-[#050505]">
      <div className="max-w-4xl mx-auto px-6 pt-10">
        <BrandMark size={40} eyebrow="Event Enquiry" />
      </div>
      {children}
      <div className="max-w-4xl mx-auto px-6 pb-10">
        <SocialLinks />
      </div>
    </div>
  );
}

function DatePicker({ value, onChange, testid }) {
  const [open, setOpen] = useState(false);
  const selected = value ? new Date(value + "T00:00:00") : undefined;
  const label = selected ? selected.toLocaleDateString(undefined, { weekday: "short", day: "numeric", month: "short", year: "numeric" }) : "Pick a date";
  const toIso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button type="button" data-testid={testid} className="inp w-full flex items-center gap-2 text-left">
          <CalendarIcon className="w-3.5 h-3.5 text-[#D4AF37]" />
          <span className={selected ? "" : "text-zinc-500"}>{label}</span>
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0 bg-[#0a0a0a] border-[#2a2a2a]" align="start">
        <Calendar mode="single" selected={selected} disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))}
          onSelect={(d) => { if (d) { onChange(toIso(d)); setOpen(false); } }} initialFocus />
      </PopoverContent>
    </Popover>
  );
}

function Section({ title, children }) {
  return (
    <div className="card-lux p-6">
      <div className="label-eyebrow mb-4">{title}</div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">{children}</div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="label-eyebrow text-[10px]">{label}</Label>
      {children}
    </div>
  );
}
